import {
  ButtonMenu,
  Css,
  Icon,
  MenuItem,
  Palette,
  Tag,
  Tooltip,
  useComputed,
  useHover,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ChangeEvent, ReactNode, useRef } from "react";
import { rejectAiProposal } from "src/components/formFields/ai/utils/bindAiProposal";
import { AssetUppyFile, useUppyUploader } from "src/components/uploader/UppyUploader";
import { MaterialListing_DetailVariantFragment } from "src/routes/libraries/material-catalog/components/materialListingDetail.generated";
import { MaterialVariantFormValue } from "src/routes/libraries/material-catalog/components/materialListingForm";
import {
  CompareVariantImageModal,
  findImageProposal,
  proposedImageChange,
} from "src/routes/libraries/material-catalog/material-listing-page/components/CompareVariantImageModal";
import { useMaterialListingContext } from "src/routes/libraries/material-catalog/material-listing-page/components/material-listing-context/MaterialListingContext";
import { MATERIAL_PHOTO_EMPTY_STATE_IMG_URL } from "src/utils/constants";

/**
 * Fixed tile size: five of these plus their gaps span the form's 720px centered column, and narrower
 * viewports just fit fewer per row — the tiles wrap rather than shrink.
 */
const THUMBNAIL_SIZE_PX = 128;

/**
 * The listing's variants as a wrapping row of thumbnails, in the context's display order.
 *
 * Selecting a saved variant is a *navigation* — `setSelectedVariant` pushes the variant into the URL — so a
 * particular finish is linkable. An unsaved row from Add Variant has no id yet, so it stays off the URL
 * and is selected via `selectPendingVariant` instead. Edits are not at risk either way: the form spans
 * the whole listing, so switching only changes which `formState.variants.rows[i]` the fields below are
 * bound to.
 */
type VariantThumbnailStripProps = {
  /** Outlines the selected tile in purple instead of blue to match the rest of an AI review. */
  aiMode?: boolean;
};

export function VariantThumbnailStrip(props: VariantThumbnailStripProps) {
  const { aiMode = false } = props;
  const {
    variants,
    selectedVariant,
    selectedVariantForm,
    setSelectedVariant,
    formState,
    isEditingPendingVariant,
    selectPendingVariant,
    discardPendingVariant,
  } = useMaterialListingContext();
  const tid = useTestIds({}, "materialListing");
  const inputRef = useRef<HTMLInputElement>(null);

  // Paired up once here so each tile is handed its row, rather than searching the list itself.
  const formRowsById = useComputed(
    () => new Map(formState.variants.rows.map((row) => [row.id.value, row])),
    [formState],
  );
  // `addVariant` only ever adds one unsaved row, so there is a single pending tile rather than a list.
  const pendingRow = useComputed(() => formState.variants.rows.find((row) => !row.id.value), [formState]);

  const uppy = useUppyUploader({
    onFinish: (file) => {
      if (selectedVariantForm) changeVariantPhoto(selectedVariantForm, file);
    },
    autoProceed: true,
  });

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const [file] = event.target.files || [];
    if (file) uppy.addFile({ name: file.name, size: file.size, type: file.type, data: file });
    event.target.value = "";
  }

  function openPhotoPicker() {
    inputRef.current?.click();
  }

  return (
    <div {...tid} css={Css.df.fww.gap2.$}>
      {variants.map((variant) => (
        <VariantThumbnail
          key={variant.id}
          variant={variant}
          formRow={formRowsById.get(variant.id)}
          selected={!isEditingPendingVariant && variant.id === selectedVariant?.id}
          aiMode={aiMode}
          onSelect={() => setSelectedVariant(variant.id)}
          onChangePhoto={openPhotoPicker}
        />
      ))}
      {pendingRow && (
        <PendingVariantThumbnail
          formRow={pendingRow}
          selected={isEditingPendingVariant}
          aiMode={aiMode}
          onSelect={selectPendingVariant}
          onUpload={openPhotoPicker}
          onRemove={discardPendingVariant}
        />
      )}
      <input
        {...tid.photoInput}
        css={Css.dn.$}
        ref={inputRef}
        type="file"
        accept={["image/jpeg", "image/png", "image/gif"].join()}
        onChange={handleFileChange}
      />
    </div>
  );
}

type ThumbnailTileProps = {
  formRow: ObjectState<MaterialVariantFormValue> | undefined;
  selected: boolean;
  /** Selected outline is purple for an AI review. */
  aiMode: boolean;
  /** Saved variants use `thumbnail`; the unsaved Add Variant row uses `pending`. */
  kind: "thumbnail" | "pending";
  /** The unsaved row is outlined rather than solid, to read as a placeholder. */
  dashed?: boolean;
  /** Omit to leave the photo menu off entirely, i.e. on the empty upload tile. */
  onChangePhoto?: VoidFunction;
  /** Opens the side-by-side review of an imported photo. Omit when there is none to review. */
  onCompare?: VoidFunction;
  /** Drops an unsaved Add Variant row. Shown in the same hover menu as the photo actions. */
  onRemove?: VoidFunction;
  /** The swatch itself — an image for a saved variant, or the upload prompt for the new row. */
  children: ReactNode;
};

/**
 * The chrome every tile shares: the fixed square frame, its corner badges, and the photo menu that
 * appears on hover.
 *
 * A variant's fields are only visible while it is selected, so the strip has to advertise on the tile
 * whatever the reviewer would otherwise have to click through to find.
 */
function ThumbnailTile(props: ThumbnailTileProps) {
  const { formRow, selected, aiMode, kind, dashed = false, onChangePhoto, onCompare, onRemove, children } = props;
  const tid = useTestIds({}, "materialListing");
  const { hoverProps, isHovered } = useHover({});
  const needsAttention = useComputed(() => !!formRow?.dirty, [formRow]);
  const hasAiProposals = useComputed(() => !!formRow?.fieldProposals.rows.nonEmpty, [formRow]);
  const hasPhoto = useComputed(() => !!formRow?.images.rows.nonEmpty, [formRow]);
  const menuItems: MenuItem[] = [
    ...(onCompare ? [{ label: "Compare Changes", ai: true, onClick: onCompare }] : []),
    ...(onChangePhoto
      ? [
          { label: "Change Photo", icon: "pencil" as const, onClick: onChangePhoto },
          {
            label: "Delete Photo",
            icon: "trash" as const,
            onClick: () => formRow && deleteVariantPhoto(formRow),
            disabled: hasPhoto ? undefined : "This variant has no photo to delete.",
          },
        ]
      : []),
    ...(onRemove ? [{ label: "Remove Variant", onClick: onRemove }] : []),
  ];

  return (
    <div
      {...(kind === "pending" ? tid.pending : tid.thumbnail)}
      {...hoverProps}
      css={{
        ...Css.relative.fs0.sqPx(THUMBNAIL_SIZE_PX).br8.ba.bgWhite.p1.$,
        ...(dashed ? Css.bsDashed.$ : {}),
        ...(selected ? (aiMode ? Css.bcPurple600.bw2.$ : Css.bcBlue600.bw2.$) : Css.bcGray300.$),
      }}
    >
      {children}

      {/* Left corner, so the hover menu on the right can't cover it while its tooltip is being read. */}
      {needsAttention && (
        <div css={Css.absolute.topPx(6).leftPx(6).$}>
          <Tooltip title="This variant has unsaved changes. Save to confirm." placement="top">
            <div {...tid.needsAttention} css={Css.sqPx(20).br4.bgRed100.df.aic.jcc.$}>
              <Icon icon="error" color={Palette.Red600} inc={2} />
            </div>
          </Tooltip>
        </div>
      )}

      {(hasAiProposals || (selected && menuItems.nonEmpty)) && (
        <div css={Css.absolute.topPx(6).rightPx(6).df.aic.gapPx(4).hPx(20).mwPx(20).$}>
          {hasAiProposals && (
            <Tag
              {...tid.aiBadge}
              // For accessibility support
              text="This variant has AI proposals to review"
              preventTooltip
              icon="aiStar"
              type="ai"
              iconOnly
            />
          )}
          {selected && menuItems.nonEmpty && (
            // Covers the AI badge
            <div css={Css.absolute.top0.left0.w100.h100.z1.bgWhite.br4.df.aic.jcfe.if(!isHovered).vh.$}>
              <ButtonMenu {...tid.photoMenu} trigger={{ icon: "verticalDots" }} items={menuItems} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type VariantThumbnailProps = {
  variant: MaterialListing_DetailVariantFragment;
  formRow: ObjectState<MaterialVariantFormValue> | undefined;
  selected: boolean;
  aiMode: boolean;
  onSelect: VoidFunction;
  onChangePhoto: VoidFunction;
};

function VariantThumbnail(props: VariantThumbnailProps) {
  const { variant, formRow, selected, aiMode, onSelect, onChangePhoto } = props;
  const tid = useTestIds({}, "materialListing");
  const { openModal } = useModal();
  const imageSrc = useComputed(() => featuredImageSrc(formRow), [formRow]);
  // An import's photo is an "update" only when it displaced one
  const isImageUpdate = useComputed(() => !!proposedImageChange(formRow)?.original, [formRow]);

  function openCompare() {
    if (formRow) {
      openModal({ content: <CompareVariantImageModal formRow={formRow} />, size: "lg", drawHeaderBorder: true });
    }
  }

  return (
    <div css={Css.df.fdc.$}>
      <ThumbnailTile
        kind="thumbnail"
        formRow={formRow}
        selected={selected}
        aiMode={aiMode}
        onChangePhoto={onChangePhoto}
        onCompare={isImageUpdate ? openCompare : undefined}
      >
        <Tooltip title={variant.displayName} placement="top">
          <button
            {...tid.swatch}
            type="button"
            aria-label={variant.displayName}
            aria-pressed={selected}
            css={Css.w100.h100.bn.p0.br8.oh.bgTransparent.cursorPointer.$}
            onClick={onSelect}
          >
            <img {...tid.image} css={Css.w100.h100.objectContain.$} src={imageSrc} alt="" loading="lazy" />
          </button>
        </Tooltip>
      </ThumbnailTile>
      {isImageUpdate && (
        <div {...tid.updatedImage} css={Css.xsSb.purple600.mtPx(4).$}>
          Updated Image
        </div>
      )}
    </div>
  );
}

type PendingVariantThumbnailProps = {
  formRow: ObjectState<MaterialVariantFormValue>;
  selected: boolean;
  aiMode: boolean;
  onSelect: VoidFunction;
  onUpload: VoidFunction;
  onRemove: VoidFunction;
};

function PendingVariantThumbnail(props: PendingVariantThumbnailProps) {
  const { formRow, selected, aiMode, onSelect, onUpload, onRemove } = props;
  const tid = useTestIds({}, "materialListing");
  const imageSrc = useComputed(() => pendingImageSrc(formRow), [formRow]);

  return (
    <div css={Css.df.fdc.$}>
      <ThumbnailTile
        kind="pending"
        formRow={formRow}
        selected={selected}
        aiMode={aiMode}
        dashed
        // Until there's a photo the tile *is* the upload control, so there is nothing to change or delete.
        onChangePhoto={imageSrc ? onUpload : undefined}
        onRemove={onRemove}
      >
        <button
          {...tid.pendingSwatch}
          type="button"
          aria-label={selected && !imageSrc ? "Upload variant photo" : "New variant"}
          aria-pressed={selected}
          css={Css.w100.h100.bn.p0.br8.oh.df.aic.jcc.bgBlue50.cursorPointer.$}
          onClick={() => {
            // Already on this row: the tile is the upload control. Coming from another variant: just select.
            if (selected && !imageSrc) onUpload();
            else onSelect();
          }}
        >
          {imageSrc ? (
            <img {...tid.pendingImage} css={Css.w100.h100.objectContain.$} src={imageSrc} alt="" loading="lazy" />
          ) : (
            <Icon icon="cloudUpload" color={Palette.Blue700} />
          )}
        </button>
      </ThumbnailTile>
      {selected && (
        <div {...tid.editing} css={Css.blue600.sm.mtPx(4).tac.$}>
          Editing
        </div>
      )}
    </div>
  );
}

function featuredImageSrc(formRow: ObjectState<MaterialVariantFormValue> | undefined): string {
  const featured = formRow?.images.rows.sortBy((img) => img.sortOrder.value ?? 0).first;
  return featured?.attachmentUrl.value || featured?.downloadUrl.value || MATERIAL_PHOTO_EMPTY_STATE_IMG_URL;
}

function pendingImageSrc(formRow: ObjectState<MaterialVariantFormValue>): string | undefined {
  const featured = formRow.images.rows.sortBy((img) => img.sortOrder.value ?? 0).first;
  return featured?.attachmentUrl.value || featured?.downloadUrl.value || undefined;
}

function changeVariantPhoto(variant: ObjectState<MaterialVariantFormValue>, file: AssetUppyFile) {
  const featured = variant.images.rows.sortBy((img) => img.sortOrder.value ?? 0).first;
  if (featured) variant.images.remove(featured.value);
  const downloadUrl = file.meta.downloadUrl as string;
  variant.images.add({
    sortOrder: 1,
    downloadUrl,
    attachmentUrl: downloadUrl,
    asset: { fileName: file.name, s3Key: file.meta.s3Key as string, contentType: file.type, sizeInBytes: file.size },
  });
  rejectAiProposal(findImageProposal(variant));
}

function deleteVariantPhoto(variant: ObjectState<MaterialVariantFormValue>) {
  const featured = variant.images.rows.sortBy((img) => img.sortOrder.value ?? 0).first;
  if (featured) variant.images.remove(featured.value);
  rejectAiProposal(findImageProposal(variant));
}
